import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Eye, FileText, RefreshCw, Send } from 'lucide-react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { LoadError } from '../../../components/layout/LoadError';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useConsequence } from '../../../components/ui/ConsequencePreview';
import { useToast } from '../../../components/ui/toast';
import { useDocKitBatch, usePublishDocKitBatch, useRetryDocument } from '../../../hooks/doc-kit';
import { ApiError } from '../../../lib/api-client';
import type { DocumentRow, DocKitResponse } from '@payroll/shared';

function isUnavailable<T extends object>(data: DocKitResponse<T> | undefined): data is { unavailable: string } {
  return Boolean(data && 'unavailable' in data);
}

function actionError(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function documentState(document: DocumentRow): { label: string; className: string } {
  if (document.job?.state === 'pending') return { label: 'ร่าง · รอทำ PDF', className: 'bg-amber-50 text-amber-800' };
  if (document.job?.state === 'running') return { label: 'ร่าง · กำลังทำ PDF', className: 'bg-blue-50 text-blue-800' };
  if (document.job?.state === 'failed') return { label: 'ร่าง · ทำ PDF ไม่สำเร็จ', className: 'bg-red-50 text-red-700' };
  const byDocument = {
    draft: { label: 'ร่าง', className: 'bg-gray-100 text-gray-700' },
    approved: { label: 'ตรวจแล้ว', className: 'bg-brand-50 text-brand-700' },
    published: { label: 'เผยแพร่แล้ว', className: 'bg-green-50 text-green-700' },
    archived: { label: 'เก็บถาวร', className: 'bg-gray-100 text-gray-500' },
    cancelled: { label: 'ยกเลิกแล้ว', className: 'bg-gray-100 text-gray-500' },
  } as const;
  return byDocument[document.status];
}

function BatchSkeleton() {
  return <div className="space-y-4 animate-pulse" aria-label="กำลังโหลดชุดเอกสาร"><div className="card h-44 bg-gray-100" />{[0, 1, 2].map((item) => <div key={item} className="card h-20 bg-gray-100" />)}</div>;
}

export function DocKitBatch() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const consequence = useConsequence();
  const batchQuery = useDocKitBatch(id);
  const publish = usePublishDocKitBatch();
  const retry = useRetryDocument();
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  async function retryDocument(document: DocumentRow) {
    if (retryingId || publishing) return;
    setRetryingId(document.id);
    try {
      const response = await retry.mutateAsync({ id: document.id });
      if (isUnavailable(response)) { toast.error(response.unavailable); return; }
      toast.success('สั่งทำ PDF ใหม่แล้ว');
    } catch (error) {
      toast.error(actionError(error, 'สั่งทำ PDF ใหม่ไม่สำเร็จ กรุณาลองใหม่'));
    } finally {
      setRetryingId(null);
    }
  }

  async function publishApproved() {
    if (!id || publishing || retryingId) return;
    const data = batchQuery.data;
    if (!data || isUnavailable(data)) return;
    const approved = data.documents.filter((document) => document.status === 'approved');
    if (approved.length === 0) {
      toast.error('ยังไม่มีเอกสารที่ตรวจแล้วให้เผยแพร่');
      return;
    }
    const confirmed = await consequence({
      title: 'เผยแพร่เอกสารที่ตรวจแล้ว',
      intro: 'พนักงานในขอบเขตของเอกสารจะเห็นเอกสารและต้องรับทราบหรือลงนามตามที่กำหนด',
      lines: [{ label: 'เอกสารที่ตรวจแล้ว', value: `${approved.length} ฉบับ`, emphasis: true }],
      effects: ['เอกสารที่เผยแพร่แล้วแก้ไขเนื้อหาไม่ได้', 'หากต้องแก้ไข ต้องออกเอกสารฉบับใหม่'],
      confirmText: 'เผยแพร่เอกสาร',
    });
    if (!confirmed) return;
    setPublishing(true);
    try {
      const response = await publish.mutateAsync({ id, body: {} });
      if (isUnavailable(response)) { toast.error(response.unavailable); return; }
      toast.success(response.skipped.length > 0 ? 'เผยแพร่เอกสารที่พร้อมแล้ว บางฉบับยังไม่ผ่านเงื่อนไข' : 'เผยแพร่เอกสารแล้ว');
    } catch (error) {
      toast.error(actionError(error, 'เผยแพร่เอกสารไม่สำเร็จ กรุณาลองใหม่'));
    } finally {
      setPublishing(false);
    }
  }

  if (batchQuery.isLoading) return <AdminLayout title="ชุดเอกสาร"><BatchSkeleton /></AdminLayout>;
  if (batchQuery.error || !batchQuery.data) return <AdminLayout title="ชุดเอกสาร"><LoadError error={batchQuery.error} onRetry={() => void batchQuery.refetch()} /></AdminLayout>;
  if (isUnavailable(batchQuery.data)) return <AdminLayout title="ชุดเอกสาร"><div className="card border-l-4 border-l-amber-400 p-4 text-sm">{batchQuery.data.unavailable}</div></AdminLayout>;

  const { batch, documents } = batchQuery.data;
  const working = documents.filter((document) => document.job?.state === 'pending' || document.job?.state === 'running').length;
  const etaSeconds = working * 8;
  const counts = batch.counts;
  const busy = publishing || Boolean(retryingId);
  return (
    <AdminLayout title="ชุดเอกสาร" subtitle="ติดตามการจัดทำ ตรวจ และเผยแพร่เอกสาร">
      <div className="max-w-5xl space-y-4">
        <section className="card p-5 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap"><div><h2 className="font-semibold">ความคืบหน้าชุดเอกสาร</h2><p className="mt-1 text-sm text-gray-600">มีผลตั้งแต่ {batch.effective_from}</p></div><span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700">{batch.state === 'open' ? 'กำลังดำเนินการ' : batch.state === 'closed' ? 'เสร็จสิ้น' : 'ยกเลิกแล้ว'}</span></div>
          <p className="text-sm font-medium text-gray-800">ทำ PDF แล้ว {counts.pdf_ready}/{counts.total} · ตรวจแล้ว {counts.approved} · เผยแพร่ {counts.published} · ติดขัด {counts.failed}</p>
          <div className="h-2.5 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-brand-600" style={{ width: `${counts.total > 0 ? Math.min(100, Math.max(0, Math.round((counts.pdf_ready / counts.total) * 100))) : 0}%` }} /></div>
          {working > 0 && <p className="text-sm text-gray-600">เหลืองานจัดทำ {working} ฉบับ · คาดว่าใช้เวลาอีกประมาณ {etaSeconds} วินาที</p>}
          <div className="flex flex-wrap gap-2"><button className="btn-cta min-h-11" disabled={busy || counts.approved === 0} onClick={() => void publishApproved()}><Send className="h-4 w-4" /> {publishing ? 'กำลังเผยแพร่' : 'เผยแพร่ฉบับที่ตรวจแล้ว'}</button><Link className="btn-secondary min-h-11" to="/hr/documents"><FileText className="h-4 w-4" /> ดูเอกสารทั้งหมด</Link></div>
        </section>

        {documents.length === 0 ? <EmptyState icon={FileText} title="ยังไม่มีเอกสารในชุดนี้" detail="ระบบยังไม่ได้สร้างเอกสารสำหรับชุดนี้" /> : (
          <section className="space-y-3" aria-label="รายการเอกสารในชุด">
            {documents.map((document) => {
              const state = documentState(document);
              return <article key={document.id} className="card p-4"><div className="flex items-start justify-between gap-3 flex-wrap"><div className="min-w-0"><h2 className="font-semibold break-words">{document.title}</h2><p className="mt-1 text-sm text-gray-600">ฉบับที่ {document.doc_version} · มีผล {document.effective_from}</p>{document.job?.state === 'failed' && document.job.last_error?.message && <p className="mt-2 text-sm text-red-700">{document.job.last_error.message}</p>}</div><span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${state.className}`}>{state.label}</span></div><div className="mt-3 flex flex-wrap gap-2"><Link className="btn-secondary min-h-11" to={`/hr/documents/${encodeURIComponent(document.id)}`}><Eye className="h-4 w-4" /> ดูและตรวจ</Link>{document.job?.state === 'failed' && <button className="btn-secondary min-h-11" disabled={busy} onClick={() => void retryDocument(document)}><RefreshCw className="h-4 w-4" /> {retryingId === document.id ? 'กำลังสั่งทำใหม่' : 'ลองอีกครั้ง'}</button>}</div></article>;
            })}
          </section>
        )}
      </div>
    </AdminLayout>
  );
}
